import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Users, Check, Share2, GripVertical, ArrowUp, ArrowDown, Pin, X, Save, Link2 } from 'lucide-react';
import { getPublicUser, getCircleStats, listPrayers, getCircle, toggleCircle, listPinnedForUser, reorderPinnedPrayers, reorderOwnCirclePrayers, fetchLiveCounts } from '../lib/api';
import { sortLiveFirst, editedTime } from '../lib/feedSort';
import { shareCircle, circleShareUrl } from '../lib/share';
import { useAuth } from '../contexts/AuthContext';
import PrayerCard from '../components/PrayerCard';

/**
 * Prayer Circle page — a public view of a specific author's prayers
 * plus a header where visitors can join / leave that author's circle.
 *
 * Route: /circle/:userId
 *
 * Reuses the existing card grid layout (matches "My Prayers") and the
 * shared PrayerCard component, so no divergent styling.
 */
const CirclePage = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, openAuthModal } = useAuth();
  // View-only mode: when navigated from the "Joined Circles" list, the
  // visitor is already a member, so we hide the Join/Unjoin action
  // button to match the "My Circle" (self) layout. Any other entry
  // point (avatar clicks on prayer cards, direct URL, etc.) keeps the
  // action button so non-members can still join.
  const viewOnly = Boolean(location.state?.fromJoinedCircles);

  const [profile, setProfile] = useState(null);
  const [prayers, setPrayers] = useState([]);
  // Fresh live-praying counts per prayer id, refreshed every 5s. Fed
  // into sortLiveFirst() below so any prayer with someone currently
  // in the immersive pray-now overlay floats to the top of the grid.
  const [liveCountsById, setLiveCountsById] = useState({});
  // Pinned prayers kept as a SEPARATE ordered list so the owner can
  // drag-reorder them without disturbing the interleaved feed below.
  // The order here already reflects the owner's saved `position`
  // (backend sorts by it) — legacy pins with no position fall to the
  // tail sorted by pinned_at desc.
  const [pinnedList, setPinnedList] = useState([]);
  const [circle, setCircle] = useState({ in_circle: false, is_self: false, member_count: 0 });
  // Author-only stats block: total prayers, aggregate watch count, and
  // followers. Fetched lazily on first render and only rendered when
  // the visitor is viewing THEIR OWN circle (is_self: true) so a
  // curious follower can't peek at another author's engagement.
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Reorder-pins mode (owner-only): toggles a compact drag-and-drop
  // list of pinned cards. `draftOrder` holds the working sequence
  // while the user is dragging; on Save it's POSTed to the reorder
  // endpoint and merged back into `pinnedList`.
  const [reorderMode, setReorderMode] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);
  const [reorderSaving, setReorderSaving] = useState(false);
  const dragIndexRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [prof, ownList, pinnedList, circleStatus, statsResp] = await Promise.all([
        getPublicUser(userId),
        listPrayers({ user_id: userId, limit: 60 }),
        listPinnedForUser(userId),
        getCircle(userId).catch(() => ({ in_circle: false, is_self: false, member_count: 0 })),
        // Circle stats — surfaced only on the owner's own view. Silent
        // fallback keeps the header from breaking if the endpoint hiccups.
        getCircleStats(userId).catch(() => null),
      ]);
      // Split into "included" (pinned collection, shown AFTER the
      // owner's own prayers) and "own" (author's own prayers, sorted
      // by created_at desc). The included list order comes straight
      // from the API — the backend honours the owner's `position`
      // field, falling back to newest-first for legacy rows.
      const pinnedArr = Array.isArray(pinnedList) ? pinnedList : [];
      const pinnedIds = new Set(pinnedArr.map((p) => p.id));
      const ownArr = (Array.isArray(ownList) ? ownList : [])
        .filter((p) => !pinnedIds.has(p.id))
        // Owner-set `circle_position` wins. Prayers without one fall to
        // the tail sorted by created_at desc so newly-added prayers
        // still surface near the top when the owner hasn't reordered.
        .sort((a, b) => {
          const ap = Number.isFinite(a.circle_position) ? a.circle_position : Number.POSITIVE_INFINITY;
          const bp = Number.isFinite(b.circle_position) ? b.circle_position : Number.POSITIVE_INFINITY;
          if (ap !== bp) return ap - bp;
          const at = a.created_at || '';
          const bt = b.created_at || '';
          if (at === bt) return 0;
          return at < bt ? 1 : -1;
        });
      const merged = [
        ...ownArr.map((p) => ({ ...p, __isPinnedOnCircle: false })),
        ...pinnedArr.map((p) => ({ ...p, __isPinnedOnCircle: p.user_id !== userId })),
      ];
      setProfile(prof);
      setPrayers(merged);
      setPinnedList(pinnedArr);
      setCircle(circleStatus || {});
      setStats(statsResp || null);
    } catch (err) {
      console.error('[CirclePage] load failed', err);
      setError(err?.response?.status === 404 ? 'This circle could not be found.' : 'Failed to load this circle.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Card updates from the ⋮ menu (e.g. Archive). Archived prayers are
  // hidden everywhere, so drop them from the circle list immediately;
  // other updates merge in place.
  const handleCardUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    if (updated.visibility === 'archived') {
      setPrayers((prev) => prev.filter((p) => p.id !== updated.id));
      setPinnedList((prev) => prev.filter((p) => p.id !== updated.id));
    } else {
      setPrayers((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    }
  }, []);

  // Re-fetch when a pin toggle for THIS owner's circle happens elsewhere
  // (e.g., from a card menu) so the feed stays fresh in real time.
  useEffect(() => {
    const onPinChanged = (ev) => {
      const d = ev?.detail || {};
      if (d.userId && d.userId === userId) load();
    };
    window.addEventListener('pin:changed', onPinChanged);
    return () => window.removeEventListener('pin:changed', onPinChanged);
  }, [userId, load]);

  const handleToggle = async () => {
    if (!user) { openAuthModal(); return; }
    if (circle.is_self || busy) return;
    const wasInCircle = !!circle.in_circle;
    setBusy(true);
    try {
      const next = await toggleCircle(userId);
      setCircle((c) => ({ ...c, ...next }));
      // Broadcast so any mounted CircleHeart / card menu for the same
      // author updates in real time without a refetch.
      window.dispatchEvent(new CustomEvent('circle:changed', {
        detail: { authorUserId: userId, in_circle: !!next?.in_circle },
      }));
      // If the visitor just unjoined this circle, send them back to
      // their "Joined Circles" list. The user is no longer a member,
      // so keeping them on this page would leave a stale action button.
      if (wasInCircle && !next?.in_circle) {
        navigate('/circles');
      }
    } catch (err) {
      console.error('[CirclePage] toggle failed', err);
    } finally {
      setBusy(false);
    }
  };

  const displayName = profile?.handle ? `@${profile.handle}` : (profile?.name || 'This member');
  const nameForHeading = profile?.name || profile?.handle || 'This member';
  const initial = (profile?.handle || profile?.name || '?')[0]?.toUpperCase();
  // Show the Join/Unjoin action button when the visitor is not the owner AND
  // either (a) they're already a member — so they can Unjoin — or (b) we're
  // not in view-only mode. This ensures the Unjoin button always appears next
  // to Share Circle for existing members, including when the page was reached
  // from the "Joined Circles" list.
  const canToggle = !circle.is_self && (circle.in_circle || !viewOnly);

  // Local UI state for the header's Share Circle button. Reverts to
  // the idle icon a moment after a successful share/copy so the
  // button can be reused without a page interaction.
  const [circleShareStatus, setCircleShareStatus] = useState(null);
  const handleShareCircle = async () => {
    // Fire the OS share sheet directly — no intermediate prompt.
    const status = await shareCircle({
      user_id: userId,
      handle: profile?.handle,
      name: profile?.name,
    });
    setCircleShareStatus(status);
    if (status === 'shared' || status === 'copied') {
      setTimeout(() => setCircleShareStatus(null), 1800);
    }
  };

  // Dedicated "Copy Link" button — clipboard-only, no OS share sheet.
  // Uses the same URL builder as `shareCircle` so recipients land on
  // the identical /circle/:userId route. Own status state so it can
  // sit side-by-side with Share Circle and give independent feedback.
  const [circleCopyStatus, setCircleCopyStatus] = useState(null); // 'copied' | 'error' | null
  const handleCopyCircleLink = async () => {
    const url = circleShareUrl({
      user_id: userId,
      handle: profile?.handle,
      name: profile?.name,
    });
    if (!url) {
      setCircleCopyStatus('error');
      setTimeout(() => setCircleCopyStatus(null), 1800);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCircleCopyStatus('copied');
    } catch (err) {
      console.warn('copy circle link failed', err);
      setCircleCopyStatus('error');
    }
    setTimeout(() => setCircleCopyStatus(null), 1800);
  };

  // ---------- Reorder-Pins (owner-only) ----------
  // Enter reorder mode: snapshot the current pin order into `draftOrder`
  // so the drag interactions never touch the live list until the user
  // hits Save. Cancel discards the draft; Save persists via the reorder
  // endpoint and merges the new order into both `pinnedList` and the
  // merged `prayers` array.
  const openReorder = () => {
    setDraftOrder(pinnedList.map((p) => p.id));
    setReorderMode(true);
  };
  const cancelReorder = () => {
    if (reorderSaving) return;
    setReorderMode(false);
    setDraftOrder([]);
  };
  const moveDraftItem = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setDraftOrder((prev) => {
      if (fromIdx < 0 || fromIdx >= prev.length) return prev;
      const next = prev.slice();
      const [item] = next.splice(fromIdx, 1);
      next.splice(Math.max(0, Math.min(next.length, toIdx)), 0, item);
      return next;
    });
  };
  const onDragStart = (idx) => (e) => {
    dragIndexRef.current = idx;
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); } catch { /* Firefox requires setData */ }
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch { /* older Safari */ }
    const from = dragIndexRef.current;
    if (from == null || from === idx) return;
    moveDraftItem(from, idx);
    dragIndexRef.current = idx;
  };
  const onDragEnd = () => { dragIndexRef.current = null; };
  const saveReorder = async () => {
    if (reorderSaving) return;
    setReorderSaving(true);
    try {
      await reorderPinnedPrayers(draftOrder);
      // Re-sort the local pinnedList to match, and rebuild the merged
      // prayers array so the visible feed reflects the new order without
      // a full page reload.
      const byId = new Map(pinnedList.map((p) => [p.id, p]));
      const newPinned = draftOrder.map((id) => byId.get(id)).filter(Boolean);
      setPinnedList(newPinned);
      setPrayers((prev) => {
        const pinnedSet = new Set(newPinned.map((p) => p.id));
        const rest = prev.filter((p) => !pinnedSet.has(p.id));
        return [
          ...rest,
          ...newPinned.map((p) => ({ ...p, __isPinnedOnCircle: p.user_id !== userId })),
        ];
      });
      setReorderMode(false);
      setDraftOrder([]);
    } catch (err) {
      console.error('[CirclePage] reorder failed', err);
      alert(err?.response?.data?.detail || 'Could not save the new order. Please try again.');
    } finally {
      setReorderSaving(false);
    }
  };

  // ---------- Move Up / Move Down (⋮ menu, self-view only) ----------
  // Reorders the prayer within its own section (pinned cards reorder
  // among pins, own cards reorder among own). We optimistically update
  // the visible feed first, then persist to the section-specific
  // endpoint. On failure we swap back and surface a lightweight alert.
  //
  // The Circle Page renders `prayers` as `[...ownArr, ...pinnedList]`,
  // so we split the merged array on `__isPinnedOnCircle` to figure out
  // which cohort a card belongs to.
  const handleMoveCard = async (prayer, direction) => {
    if (!prayer || (direction !== 'up' && direction !== 'down')) return;
    const isPin = Boolean(prayer.__isPinnedOnCircle);
    // Snapshot the current merged order for rollback on failure.
    const prevPrayers = prayers;
    const prevPinned = pinnedList;
    // Split into cohorts (must match the merge order used in `load`).
    const pinnedCohort = prevPrayers.filter((p) => p.__isPinnedOnCircle);
    const ownCohort = prevPrayers.filter((p) => !p.__isPinnedOnCircle);
    const cohort = isPin ? pinnedCohort : ownCohort;
    const idx = cohort.findIndex((p) => p.id === prayer.id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= cohort.length) return; // boundary
    const nextCohort = cohort.slice();
    const [row] = nextCohort.splice(idx, 1);
    nextCohort.splice(targetIdx, 0, row);
    // Recompose the merged view — own first, then included.
    const nextMerged = isPin
      ? [...ownCohort, ...nextCohort]
      : [...nextCohort, ...pinnedCohort];
    setPrayers(nextMerged);
    if (isPin) setPinnedList(nextCohort);
    try {
      const ids = nextCohort.map((p) => p.id);
      if (isPin) {
        await reorderPinnedPrayers(ids);
      } else {
        await reorderOwnCirclePrayers(ids);
      }
    } catch (err) {
      console.error('[CirclePage] move card failed', err);
      // Rollback on failure so the UI never lies about what's persisted.
      setPrayers(prevPrayers);
      if (isPin) setPinnedList(prevPinned);
      alert(err?.response?.data?.detail || 'Could not save the new order. Please try again.');
    }
  };

  return (
    <div data-testid="circle-page">

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 py-12 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading circle…
        </div>
      ) : error ? (
        <div className="mx-4 sm:mx-6 mt-6 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 p-4" data-testid="circle-error">
          {error}
        </div>
      ) : (
        <>
          {/* ---- Header ----
              Same shell as My Prayers / My Lit Candles: avatar badge on
              the left, title + subtitle in the middle, action buttons
              on their own row underneath. The owner avatar takes the
              slot the icon badge occupies on the sibling pages so the
              visual rhythm stays identical. */}
          <div
            className="px-4 sm:px-6 pt-6 pb-4 border-b border-zinc-200 dark:border-zinc-800"
            data-testid="circle-header"
          >
            <div className="flex items-center gap-3">
              {profile?.picture ? (
                <img
                  src={profile.picture}
                  alt={displayName}
                  className="w-[90px] h-[90px] rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-[90px] h-[90px] rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 text-lg font-medium flex-shrink-0">
                  {initial}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h1
                  className="text-2xl font-bold break-words sm:truncate"
                  data-testid="circle-title"
                >
                  {nameForHeading} - Circle
                </h1>
                <p
                  className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5 truncate"
                  data-testid="circle-owner-handle"
                >
                  {displayName}
                  {profile?.show_circle_stats && (
                    <>
                      <span className="mx-1.5 text-zinc-300 dark:text-zinc-700">•</span>
                      <span data-testid="circle-member-count">
                        <span className="font-semibold text-zinc-700 dark:text-zinc-200">
                          {Number(circle.member_count) || 0}
                        </span>
                        {' '}{(Number(circle.member_count) || 0) === 1 ? 'member' : 'members'}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* ---- Bio / mission statement ----
                Sits between the header row and the actions so a first-
                time visitor reads name → bio → CTA in a natural top-
                down flow. Hidden when no bio is set. */}
            {profile?.bio && (
              <p
                data-testid="circle-bio"
                className="mt-3 italic text-sm sm:text-base leading-relaxed text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap max-w-2xl"
              >
                {profile.bio}
              </p>
            )}

            {/* ---- Circle Stats (author-controlled, public) ----
                Renders for every visitor when the AUTHOR of this
                Circle has flipped `show_circle_stats` on in their
                own Settings. The toggle is opt-in (default off) so
                authors decide whether to publish their engagement
                numbers. Rendered as pill-cards so the layout stays
                calm and doesn't compete with the primary Join /
                Share CTA buttons underneath. */}
            {profile?.show_circle_stats && stats && (
              <div
                data-testid="circle-stats"
                className="mt-4 grid grid-cols-3 gap-2 sm:gap-3 max-w-lg"
              >
                {[
                  { key: 'total_prayers', label: 'Prayers', value: stats.total_prayers, testid: 'circle-stats-prayers' },
                  { key: 'total_watch_count', label: 'Total prayed', value: stats.total_watch_count, testid: 'circle-stats-watch' },
                  { key: 'followers', label: 'Followers', value: stats.followers, testid: 'circle-stats-followers' },
                ].map((s) => (
                  <div
                    key={s.key}
                    data-testid={s.testid}
                    className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-3 py-2.5 text-center"
                  >
                    <div className="text-lg sm:text-xl font-semibold text-zinc-900 dark:text-white leading-tight">
                      {Number(s.value || 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mt-0.5">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Actions row — same underneath-the-heading pattern as My
                Prayers, so the two pages share a rhythm. Button order:
                Share Circle → Copy Link → Join/Unjoin so the identity
                actions come first and the membership toggle sits at the
                end where it reads as a decision point. */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleShareCircle}
                data-testid="circle-share-btn"
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full font-semibold text-sm transition-colors flex-shrink-0 border ${
                  circleShareStatus === 'shared' || circleShareStatus === 'copied'
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                }`}
              >
                {circleShareStatus === 'shared' && <><Check size={15} strokeWidth={2.5} /><span>Shared</span></>}
                {circleShareStatus === 'copied' && <><Check size={15} strokeWidth={2.5} /><span>Link copied</span></>}
                {circleShareStatus === 'error' && <><Share2 size={15} /><span>Try again</span></>}
                {!circleShareStatus && <><Share2 size={15} /><span>Share Circle</span></>}
              </button>
              <button
                type="button"
                onClick={handleCopyCircleLink}
                data-testid="circle-copy-link-btn"
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full font-semibold text-sm transition-colors flex-shrink-0 border ${
                  circleCopyStatus === 'copied'
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                }`}
              >
                {circleCopyStatus === 'copied' && <><Check size={15} strokeWidth={2.5} /><span>Circle link copied</span></>}
                {circleCopyStatus === 'error' && <><Link2 size={15} /><span>Try again</span></>}
                {!circleCopyStatus && <><Link2 size={15} /><span>Copy Link</span></>}
              </button>
              {canToggle && (
                <button
                  type="button"
                  onClick={handleToggle}
                  disabled={busy}
                  data-testid={circle.in_circle ? 'circle-unjoin-btn' : 'circle-join-btn'}
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-full font-semibold text-sm transition-colors flex-shrink-0 border bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-60"
                >
                  {busy ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : circle.in_circle ? (
                    <Check size={15} strokeWidth={2.5} />
                  ) : (
                    <Users size={15} strokeWidth={2.25} />
                  )}
                  <span>{circle.in_circle ? 'Unjoin Circle' : 'Join Circle'}</span>
                </button>
              )}
              {circle.is_self && pinnedList.length >= 2 && !reorderMode && (
                <button
                  type="button"
                  onClick={openReorder}
                  data-testid="circle-reorder-pins-btn"
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-full font-semibold text-sm transition-colors flex-shrink-0 border bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  <Pin size={15} strokeWidth={2.25} />
                  <span>Reorder included</span>
                </button>
              )}
            </div>
          </div>

          {/* ---- Prayers grid / Reorder-pins panel ---- */}
          <div className="px-4 sm:px-6 py-6">
            {reorderMode ? (
              <div className="max-w-2xl mx-auto" data-testid="reorder-pins-panel">
                <div className="flex items-center justify-between mb-4">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Pin size={16} className="text-amber-500" /> Reorder included prayers
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      Drag by the handle, or use the arrows. Top of the list appears first among your included prayers.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={cancelReorder}
                      disabled={reorderSaving}
                      data-testid="reorder-cancel-btn"
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
                    >
                      <X size={14} /> Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveReorder}
                      disabled={reorderSaving}
                      data-testid="reorder-save-btn"
                      className="inline-flex items-center gap-1 h-9 px-4 rounded-full text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
                    >
                      {reorderSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      Save order
                    </button>
                  </div>
                </div>
                <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
                  {draftOrder.map((pid, idx) => {
                    const p = pinnedList.find((x) => x.id === pid);
                    if (!p) return null;
                    const isFirst = idx === 0;
                    const isLast = idx === draftOrder.length - 1;
                    return (
                      <li
                        key={pid}
                        draggable
                        onDragStart={onDragStart(idx)}
                        onDragOver={onDragOver(idx)}
                        onDragEnd={onDragEnd}
                        onDrop={onDragEnd}
                        data-testid={`reorder-row-${pid}`}
                        className="flex items-center gap-3 px-3 py-3 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 cursor-move"
                      >
                        <GripVertical size={16} className="text-zinc-400 flex-shrink-0" aria-hidden="true" />
                        <span className="text-xs font-mono text-zinc-400 tabular-nums w-6 flex-shrink-0" aria-hidden="true">
                          {idx + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{p.title || 'Untitled'}</div>
                          {p.author_handle && (
                            <div className="text-xs text-zinc-500 truncate">@{p.author_handle}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => moveDraftItem(idx, idx - 1)}
                            disabled={isFirst}
                            aria-label="Move up"
                            data-testid={`reorder-up-${pid}`}
                            className="p-2 rounded-full text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowUp size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveDraftItem(idx, idx + 1)}
                            disabled={isLast}
                            aria-label="Move down"
                            data-testid={`reorder-down-${pid}`}
                            className="p-2 rounded-full text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown size={14} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : prayers.length === 0 ? (
              <p className="text-zinc-500 dark:text-zinc-400" data-testid="circle-empty">
                {displayName} hasn&apos;t shared any prayers yet.
              </p>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-x-4 gap-y-8"
                data-testid="circle-prayers-grid"
              >
                {(() => {
                  // Pre-compute cohort boundaries so each card knows
                  // whether it's the first/last within its section.
                  // Move Up on the top card and Move Down on the bottom
                  // card are hidden (disabled) via the flags below.
                  const pinnedIdsInMerge = prayers.filter((p) => p.__isPinnedOnCircle).map((p) => p.id);
                  const ownIdsInMerge = prayers.filter((p) => !p.__isPinnedOnCircle).map((p) => p.id);
                  const pinnedFirst = pinnedIdsInMerge[0];
                  const pinnedLast = pinnedIdsInMerge[pinnedIdsInMerge.length - 1];
                  const ownFirst = ownIdsInMerge[0];
                  const ownLast = ownIdsInMerge[ownIdsInMerge.length - 1];
                  return prayers.map((p) => {
                    const isPin = Boolean(p.__isPinnedOnCircle);
                    const isFirstInCohort = isPin ? p.id === pinnedFirst : p.id === ownFirst;
                    const isLastInCohort = isPin ? p.id === pinnedLast : p.id === ownLast;
                    return (
                      <PrayerCard
                        key={p.id}
                        prayer={p}
                        isPinned={isPin}
                        onUpdate={handleCardUpdate}
                        showMoveControls={Boolean(circle.is_self)}
                        canMoveUp={Boolean(circle.is_self) && !isFirstInCohort}
                        canMoveDown={Boolean(circle.is_self) && !isLastInCohort}
                        onMoveUp={(pr) => handleMoveCard(pr, 'up')}
                        onMoveDown={(pr) => handleMoveCard(pr, 'down')}
                      />
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CirclePage;
